import { useState, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import client from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useDispatch } from 'react-redux'
import * as Actions from 'redux/actions'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { useRootStore } from 'stores/RootStore'
import { LogoSettings } from '../LogoSettings/LogoSettings'
import { BrandColorPicker } from './BrandColorPicker'
import { WhiteLabelCheckoutModal } from './WhiteLabelCheckoutModal'
import { WhiteLabelCancelModal } from './WhiteLabelCancelModal'
import { Workspace } from 'types'
import { AlertTriangle } from 'blaze-ui/icons'
import { HelpCircleContained } from 'blaze-ui/icons/24'
import Styles from './WhiteLabelSettings.module.scss'

export const WhiteLabelSettings = observer(({ workspace }: { workspace: Workspace }) => {
  const [isEnabled, setIsEnabled] = useState(workspace.is_whitelabel)
  const { openModal, closeModal } = useModals()
  const dispatch = useDispatch()
  const workspaces = useWorkspaces()
  const { currentSubscription } = useRootStore().tierUsageStore

  const isEligible = useMemo(() => {
    if (!currentSubscription) return false
    return currentSubscription.status === 'active' || currentSubscription.status === 'trialing'
  }, [currentSubscription])

  useEffect(() => {
    setIsEnabled(workspace.is_whitelabel)
  }, [workspace.is_whitelabel])

  const handleToggle = () => {
    if (!isEnabled) {
      openModal(WhiteLabelCheckoutModal, {
        onConfirm: async (priceId: string, paymentMethodId?: string) => {
          try {
            const response = await WorkspaceClient.purchaseWhiteLabel(workspace.id, priceId, paymentMethodId)
            if (response.type === SUCCESS_RESPONSE_TYPE) {
              await client.updateWorkspaceSettings({ workspace: { color_scheme: 'light' } }, workspace.key)
              setIsEnabled(true)
              dispatch(
                Actions.workspacesSet(
                  workspaces.map((w) =>
                    w.key === workspace.key ? { ...w, is_whitelabel: true, color_scheme: 'light' } : w,
                  ),
                ),
              )
              showToastMessage('Success! Set up your white label settings now.', 'success', {
                type: 'link',
                variant: 'proceed',
                label: 'White Label Settings',
                to: '?settings=general',
              })
              closeModal()
            } else {
              showToastMessage('There was an error purchasing white label', 'error')
            }
          } catch (error) {
            showToastMessage('There was an error enabling white label', 'error')
          }
        },
        onCancel: closeModal,
      })
    } else {
      openModal(WhiteLabelCancelModal, {
        onConfirm: async () => {
          try {
            const response = await WorkspaceClient.cancelWhiteLabel(workspace.id)
            if (response.type === SUCCESS_RESPONSE_TYPE) {
              setIsEnabled(false)
              dispatch(
                Actions.workspacesSet(
                  workspaces.map((w) => (w.key === workspace.key ? { ...w, is_whitelabel: false } : w)),
                ),
              )
              showToastMessage(
                'White label disabled. A prorated credit will be applied to your next invoice.',
                'success',
              )
              closeModal()
            }
          } catch (error) {
            showToastMessage('There was an error disabling white label', 'error')
          }
        },
        onCancel: closeModal,
      })
    }
  }

  const getIneligibleMessage = () => {
    if (!currentSubscription) {
      return 'An active subscription is required to enable white label.'
    }
    if (currentSubscription.status !== 'active' && currentSubscription.status !== 'trialing') {
      return 'An active subscription is required to enable white label.'
    }
    return ''
  }

  return (
    <div className={`${Styles.section} ${!isEligible && !isEnabled ? Styles.disabled : ''}`}>
      <div className={Styles.headerRow}>
        <div className={Styles.headerLeft}>
          <Heading level={5}>White Label</Heading>
          <span className={Styles.addonChip}>Add-on</span>
        </div>
        <ToggleButton
          checked={isEnabled}
          handleChange={handleToggle}
          name="white-label-toggle"
          disabled={!isEligible && !isEnabled}
        />
      </div>
      <div className={Styles.description}>
        <Text variant="secondary" color="var(--dark-60)">
          Replace Blaze branding with your own across workspace header and emails.
        </Text>
        {!isEligible && !isEnabled && (
          <div className={Styles.ineligibleMessage}>
            <Text variant="secondary" color="var(--dark-40)">
              {getIneligibleMessage()}
            </Text>
          </div>
        )}
      </div>
      {!isEnabled && (
        <div className={Styles.warningBanner}>
          <AlertTriangle size={24} color="#D38E0F" />
          <Text variant="secondary" color="var(--dark-80)">
            Workspace logo updates are now part of our paid White Label feature. To upload a new logo, please upgrade to
            the White Label add-on.
          </Text>
        </div>
      )}

      {isEnabled && (
        <div className={Styles.options}>
          <div className={Styles.twoColumns}>
            <div className={Styles.column}>
              <div className={Styles.subsectionTitle}>
                <Text variant="label" color="var(--dark-40)">
                  Company Logo
                </Text>
              </div>
              <LogoSettings workspace={workspace} size="lg" />
            </div>
            <div className={Styles.column}>
              <div className={Styles.subsectionTitle}>
                <Text variant="label" color="var(--dark-40)">
                  Set Toggle and Button Color
                </Text>
              </div>
              <BrandColorPicker workspace={workspace} />
            </div>
          </div>
          <div className={Styles.helpLine}>
            <HelpCircleContained size={15} />
            <Text variant="secondary" color="var(--dark-60)">
              Questions or issues? Email us at{' '}
              <a href="mailto:support@blaze.ai" className={Styles.link}>
                support@blaze.ai
              </a>
              {' or '}
              <button
                type="button"
                className={Styles.linkButton}
                onClick={() => {
                  window.Intercom('showNewMessage', 'Hello! I need help with white label settings.')
                }}
              >
                chat with us
              </button>
              .
            </Text>
          </div>
        </div>
      )}
    </div>
  )
})
